import { pickByPaths, removeKeys } from '@tradener/lumen';
import { DateTime } from 'luxon';
import type { NonFunctionKeys } from 'utility-types';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { PayloadProps } from '@graphql/query/portfolio';

export type PortfolioProps = Replace<PayloadProps, 'excludedContracts', { [key: string]: unknown }> & {
  expandedPeriods: string[];
};
export interface PortfolioHookProps extends PortfolioProps {
  setFilter: <Key extends NonFunctionKeys<PortfolioHookProps>>(key: Key, value: PortfolioHookProps[Key]) => void;
  resetField: (key: NonFunctionKeys<PortfolioHookProps>) => void;
  removeField: (key: NonFunctionKeys<PortfolioHookProps>) => void;
}

export interface GetVariablesOptionsProps {
  showPreview?: boolean;
}

const dateTime = DateTime.now();

const initialState: PortfolioProps = {
  startYear: dateTime.year.toString(),
  endYear: dateTime.plus({ year: 1 }).year.toString(),
  tenantId: [],
  internalAccounts: [],
  energyType: [],
  priceType: [],
  expandedPeriods: [dateTime.year.toString(), dateTime.plus({ year: 1 }).year.toString()],
  contractFlags: [],
  excludedContracts: {},
  usageFactorByAngra: true,
  usageFactor: true,
  includeSuspendedContracts: false,
  accountTypes: [],
  market: [],
  modulation: [],
};

const getVariables = (state: PortfolioHookProps, options?: GetVariablesOptionsProps) => {
  const result = pickByPaths(
    state,
    'startYear',
    'endYear',
    'energyType',
    'periodType',
    'priceType',
    'operation',
    'expandedPeriods',
    'usageFactorByAngra',
    'usageFactor',
    'includeSuspendedContracts',
    'excludedContracts',
    'tenantId',
    'internalAccounts',
    'giro',
    'hasGiro',
    'contractFlags',
    'accountingPortfolio',
    'closeDateLte',
    'closeDateGte',
    'accountTypes',
    'market',
    'modulation',
  );

  return result;
};

const usePortfolio = create(
  persist<PortfolioHookProps>(
    (set) => ({
      ...initialState,
      tenantId: initialState.tenantId?.filter(Boolean),
      internalAccounts: initialState.internalAccounts?.filter(Boolean),
      setFilter: (key, value) =>
        set((state) => {
          const newValues = {
            ...state,
            [key]: value,
          };

          return newValues;
        }),
      resetField: (key) => set({ [key]: initialState[key] }),
      removeField: (key) => set((prevState) => removeKeys(prevState, key)),
    }),
    {
      name: 'PORTFOLIO',
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<PortfolioHookProps>;
        const merged = { ...currentState, ...persisted } as PortfolioHookProps;

        return merged;
      },
    },
  ),
);

export { getVariables };
export default usePortfolio;
